/**
 * `/agents/verification` — SKU A dogfood surface (Evidence & dual-agent door).
 * Writes go through review-decision.ts only. ResidualGapsCard is extracted
 * so this file stays under 1000 lines after landing waterfall + residual gaps.
 */

import * as React from "react";
import { z } from "zod";
import { createRoute, redirect } from "@tanstack/react-router";
import {
  hopLeftoverAgentsToDashboard,
  hopLeftoverLegacyRoute,
} from "./agents-unmount-hop";
import { RouterLink as Link } from "@excellent/ui/router";
import {
  Alert,
  AlertDescription,
  AlertTitle,
  Button,
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
  DataTable,
  Decision,
  IntentBadge,
  PageHeader,
  StatCard,
  StatusBadge,
  CheckingWaterfallTable,
  ResidualGapsCard,
  formatRelativeTime,
  type DataTableColumn,
} from "@excellent/ui";
import { useApi, apiCall } from "@excellent/ui/api";
import {
  evidence,
  type EvidenceTaskDetailDto,
  type EvidenceClaimDto,
  type EvidenceBundleReceiptDto,
  type EvidenceJudgmentRow,
} from "@excellent/ui/api/contracts/evidence";
import { universalVerification } from "@excellent/ui/api/contracts/universal-verification";
import {
  decideTaskReview,
  promptDecisionReason,
} from "../shared/work/review-decision";
import { routeSearchSchema } from "../shell/search";
import {
  BindingBadge,
  ReceiptsBrowser,
  RejectsBrowser,
  type BrowseSearch,
} from "../shared/agents/verification-browse";
import { VerificationRunHistory } from "../shared/agents/verification-run-history";
import { VerifierHome } from "../shared/agents/verifier-home";
import { ProofStoryCard } from "../shared/agents/proof-story-card";
import { rootRoute } from "./__root";
import { resolveReviewVerdictEvidenceRedirect } from "./verification-legacy-redirects";

const verificationSearchSchema = routeSearchSchema({
  tab: z.enum(["receipts", "rejects"]).optional(),
  binding: z.enum(["staged", "dirty"]).optional(),
  exit: z.enum(["pass", "fail"]).optional(),
  standing: z.enum(["yes", "no"]).optional(),
  reject: z.coerce.number().int().positive().optional(),
});

export const VERIFICATION_BROWSE_TAB_HREFS = [
  "/agents/verification?tab=receipts",
  "/agents/verification?tab=rejects",
] as const;
const MUTED = "type-subtext text-muted-foreground";
const NUM = "tabular-nums";

function consequenceIntent(
  tag: string | null | undefined,
): "neutral" | "warning" | "danger" {
  if (!tag || tag === "none") return "neutral";
  if (tag === "hard-internal") return "warning";
  return "danger";
}

function shortHash(h: string | null | undefined, n = 10): string {
  if (!h) return "—";
  return h.length <= n ? h : `${h.slice(0, n)}…`;
}

function predicateText(p: unknown): string {
  if (typeof p === "string") return p;
  try {
    return JSON.stringify(p);
  } catch {
    return String(p);
  }
}

function readablePredicateText(p: unknown): string {
  const raw = predicateText(p);
  try {
    const parsed = JSON.parse(raw) as unknown;
    if (parsed && typeof parsed === "object") {
      const row = parsed as Record<string, unknown>;
      const id = typeof row.id === "string" ? row.id : "claim";
      const kind = typeof row.kind === "string" ? row.kind : null;
      const result = typeof row.result === "string" ? row.result : null;
      const tier = typeof row.tier === "number" ? `T${row.tier}` : null;
      return [id, kind, tier, result].filter(Boolean).join(" · ");
    }
  } catch {
    // Non-JSON predicate text is already the clearest representation.
  }
  return raw;
}

function falsifierText(f: unknown): string {
  if (f == null) return "—";
  try {
    return typeof f === "string" ? f : JSON.stringify(f);
  } catch {
    return String(f);
  }
}

function trustIntent(
  trust: Extract<EvidenceTaskDetailDto["bundle"], { found: true }>["trust"],
): "success" | "warning" | "danger" {
  if (trust.verifyOk === false) return "danger";
  if (trust.status === "signed") return "success";
  return trust.reason?.includes("malformed") ? "danger" : "warning";
}

function trustHeadline(
  trust: Extract<EvidenceTaskDetailDto["bundle"], { found: true }>["trust"],
): string {
  if (trust.verifyOk === false) return "Offline verification failed";
  if (trust.status === "signed") return "Signed evidence pack";
  if (trust.reason?.includes("malformed")) return "Malformed trust configuration";
  return "Unsigned evidence pack";
}

function trustExplanation(
  trust: Extract<EvidenceTaskDetailDto["bundle"], { found: true }>["trust"],
): string {
  if (trust.verifyOk === false) {
    return "Do not use this pack as proof until offline verification passes against the preserved bundle.";
  }
  if (trust.status === "signed") {
    return "The bundle is signed by the configured Ed25519 trust root and can be checked independently.";
  }
  if (trust.reason?.includes("malformed")) {
    return "A signing key was configured but could not produce a valid signed pack. Fix the trust root before sharing this externally.";
  }
  return "Unsigned is explicit: the pack can still be replayed, but it does not carry a configured signing root.";
}

// ── List page ────────────────────────────────────────────────────────────────

function AgentsVerificationPage() {
  const navigate = agentsVerificationRoute.useNavigate();
  const search = agentsVerificationRoute.useSearch() as BrowseSearch;
  const tab = search.tab;
  const [soc2PreflightOpen, setSoc2PreflightOpen] = React.useState(false);
  const {
    data: listData,
    error: listError,
    isLoading: listLoading,
  } = useApi(
    evidence.list,
    { query: { status: "all", limit: 100 } },
  );
  const { data: silent } = useApi(evidence.silentApprove, { query: undefined });
  const { data: readiness } = useApi(evidence.readiness, { query: undefined });
  const { data: universalOverview } = useApi(universalVerification.overview, { query: undefined });
  const { data: soc2Preflight } = useApi(
    evidence.soc2PreflightSample,
    soc2PreflightOpen ? { query: undefined } : null,
  );
  const {
    data: soc2RunsData,
    mutate: mutateSoc2Runs,
  } = useApi(evidence.soc2PreflightRuns, soc2PreflightOpen ? { query: {} } : null);
  const {
    data: soc2ImportOnboardingState,
    mutate: mutateSoc2ImportOnboardingState,
  } = useApi(
    evidence.soc2PreflightImportOnboardingState,
    soc2PreflightOpen ? { query: {} } : null,
  );
  const { data: soc2PolicyStudioState } = useApi(
    evidence.soc2PreflightPolicyStudioState,
    soc2PreflightOpen ? { query: {} } : null,
  );
  const {
    data: soc2BatchJobsData,
    mutate: mutateSoc2BatchJobs,
  } = useApi(evidence.soc2PreflightBatchJobs, soc2PreflightOpen ? { query: undefined } : null);
  const {
    data: workflowCandidatesData,
    mutate: mutateWorkflowCandidates,
  } = useApi(evidence.workflowCandidates, { query: undefined });

  const tasks = listData?.tasks ?? [];
  const open = (id: number) => {
    void navigate({ to: "/agents/verification/$taskId", params: { taskId: String(id) } });
  };

  return (
    <div className="px-6 py-8 space-y-6">
      {tab === "receipts" ? <ReceiptsBrowser search={search} /> : null}
      {tab === "rejects" ? <RejectsBrowser search={search} /> : null}
      {tab ? null : (
        <VerifierHome
          tasks={tasks}
          silent={silent}
          readiness={readiness}
          universalOverview={universalOverview}
          soc2Preflight={soc2Preflight}
          soc2ImportOnboardingState={soc2ImportOnboardingState}
          soc2PolicyStudioState={soc2PolicyStudioState}
          soc2BatchJobs={soc2BatchJobsData?.jobs ?? []}
          soc2Runs={soc2RunsData?.runs ?? []}
          soc2PreflightOpen={soc2PreflightOpen}
          onOpenSoc2Preflight={() => setSoc2PreflightOpen(true)}
          onSoc2RunImported={() => {
            void mutateSoc2Runs();
            void mutateSoc2ImportOnboardingState();
            void mutateSoc2BatchJobs();
          }}
          workflowCandidates={workflowCandidatesData?.candidates ?? []}
          listError={listError}
          listLoading={listLoading && !listData}
          onOpen={open}
          onWorkflowCandidateSaved={() => {
            void mutateWorkflowCandidates();
          }}
        />
      )}
    </div>
  );
}

// ── Detail page ──────────────────────────────────────────────────────────────

function ClaimsTable({ claims }: { claims: EvidenceClaimDto[] }) {
  const columns: DataTableColumn<EvidenceClaimDto>[] = [
    {
      key: "seq",
      header: "Seq",
      render: (r) => <span className={NUM}>{r.seq}</span>,
    },
    {
      key: "predicate",
      header: "Claim",
      render: (r) => (
        <span className="type-body line-clamp-2" title={predicateText(r.predicate)}>
          {predicateText(r.predicate)}
        </span>
      ),
    },
    {
      key: "range",
      header: "Range",
      render: (r) => (
        <span className={`${NUM} ${MUTED}`}>
          {r.fromSeq}–{r.toSeq}
        </span>
      ),
    },
    {
      key: "falsifiers",
      header: "Falsifier",
      render: (r) => (
        <span className={`${MUTED} line-clamp-2`} title={falsifierText(r.falsifiers)}>
          {falsifierText(r.falsifiers)}
        </span>
      ),
    },
    {
      key: "chain",
      header: "Chain",
      render: (r) => (
        <code className={MUTED} title={r.chainHash ?? undefined}>
          {shortHash(r.chainHash)}
        </code>
      ),
    },
    {
      key: "actor",
      header: "Actor",
      render: (r) => <span className={MUTED}>{r.actorId}</span>,
    },
  ];

  if (claims.length === 0) {
    return (
      <p className="type-body text-muted-foreground">
        No asserted claims are bound to this workflow run yet. Capture claims
        before replay can explain the work.
      </p>
    );
  }

  return (
    <DataTable
      rows={claims}
      columns={columns}
      rowKey={(r) => `${r.sessionId}-${r.seq}`}
      wrapInCard={false}
    />
  );
}

/**
 * Dual-agent VERDICTS (`dod_judgment_receipts`) — #45872.
 *
 * ⚠️ Deliberately its own card, ABOVE the gate receipts. The two were conflated:
 * the page rendered only `gate_run_receipts` ("which commands ran, exit codes")
 * and never read the verdict table, so #45666 — five rejects in the vault —
 * contained zero occurrences of the word "reject". A verification product whose
 * surface cannot show a rejection cannot be told apart from a rubber stamp.
 * A reject must read as a reject at a glance, hence `IntentBadge` intent=reject.
 */
function JudgmentsTable({ judgments }: { judgments: EvidenceJudgmentRow[] }) {
  const columns: DataTableColumn<EvidenceJudgmentRow>[] = [
    {
      key: "verdict",
      header: "Decision",
      render: (r) => (
        <IntentBadge intent={r.verdict === "reject" ? "danger" : "success"}>
          {r.verdict}
        </IntentBadge>
      ),
    },
    {
      key: "verifierAgent",
      header: "Verifier",
      render: (r) => <code className="type-subtext">{r.verifierAgent ?? "—"}</code>,
    },
    {
      key: "note",
      header: "Note",
      render: (r) => (
        <span className="type-body line-clamp-2" title={r.note ?? ""}>
          {r.note?.trim() || "—"}
        </span>
      ),
    },
    {
      key: "createdAt",
      header: "When",
      render: (r) => (
        <span className={MUTED}>
          {formatRelativeTime(new Date(r.createdAt + "Z").toISOString())}
        </span>
      ),
    },
  ];
  if (!judgments.length)
    return (
      <p className={MUTED}>
        No verifier decision recorded for this workflow run yet.
      </p>
    );
  return (
    <DataTable
      rows={judgments}
      columns={columns}
      rowKey={(r) => `${r.createdAt}-${r.verifierAgent ?? ""}`}
      wrapInCard={false}
    />
  );
}

function ReceiptsTable({ receipts }: { receipts: EvidenceBundleReceiptDto[] }) {
  const columns: DataTableColumn<EvidenceBundleReceiptDto>[] = [
    {
      key: "command",
      header: "Check",
      render: (r) => <code className="type-subtext">{r.command}</code>,
    },
    {
      key: "exit",
      header: "Exit",
      render: (r) => (
        <IntentBadge intent={r.exitCode === 0 ? "success" : "danger"}>
          {r.exitCode}
        </IntentBadge>
      ),
    },
    {
      key: "tree",
      header: "Tree hash",
      render: (r) => (
        <code className={MUTED} title={r.treeHash}>
          {shortHash(r.treeHash, 12)}
        </code>
      ),
    },
    {
      key: "match",
      header: "vs commit",
      render: (r) =>
        r.treeMatchesCommit == null ? (
          <span className={MUTED}>—</span>
        ) : (
          <IntentBadge intent={r.treeMatchesCommit ? "success" : "danger"}>
            {r.treeMatchesCommit ? "match" : "mismatch"}
          </IntentBadge>
        ),
    },
    {
      key: "binding",
      header: "Binding",
      render: (r) => <BindingBadge binding={r.binding} />,
    },
    {
      key: "env",
      header: "Env policy",
      render: (r) => <span className={MUTED}>{r.envPolicy ?? "—"}</span>,
    },
    {
      key: "when",
      header: "When",
      render: (r) => (
        <span className={MUTED}>
          {formatRelativeTime(new Date(r.createdAt).toISOString())}
        </span>
      ),
    },
  ];

  if (receipts.length === 0) {
    return (
      <p className="type-body text-muted-foreground">
        No check receipts are bound to this workflow run yet.
      </p>
    );
  }

  return (
    <DataTable
      rows={receipts}
      columns={columns}
      rowKey={(r, i) => r.uid ?? `${r.command}-${r.createdAt}-${i}`}
      wrapInCard={false}
    />
  );
}

function EvidenceBundleCard({
  detail,
  onDownloaded,
}: {
  detail: EvidenceTaskDetailDto;
  onDownloaded?: () => void;
}) {
  const [busy, setBusy] = React.useState(false);
  const [msg, setMsg] = React.useState<string | null>(null);

  async function download() {
    setBusy(true);
    setMsg(null);
    try {
      const res = await apiCall(evidence.bundle, { params: { id: detail.taskId } });
      if (!res.ok) {
        setMsg(`Bundle unavailable: ${res.reason}`);
        return;
      }
      const blob = new Blob([res.downloadText], { type: "application/json" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `verification-run-${detail.taskId}-evidence.json`;
      a.click();
      URL.revokeObjectURL(url);
      setMsg(`Downloaded ${res.byteLength} bytes · ${res.trust.summary}`);
      onDownloaded?.();
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  async function reverify() {
    setBusy(true);
    setMsg(null);
    try {
      const res = await apiCall(evidence.verify, { params: { id: detail.taskId } });
      if (res.result.ok) {
        setMsg(
          `Verify OK · ${res.trust?.summary ?? (res.result.signed ? "signed" : "unsigned")} — ${res.result.checks.join(" · ")}`,
        );
      } else {
        setMsg(`Verify FAIL: ${res.result.mismatches.join("; ")}`);
      }
    } catch (e) {
      setMsg(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  const b = detail.bundle;
  const v = detail.verify;
  const firstClaim = detail.claims[0];
  const latestJudgment = detail.judgments[detail.judgments.length - 1];
  const failedReceipts = detail.receipts.filter((receipt) => receipt.exitCode !== 0).length;
  const passedReceipts = detail.receipts.length - failedReceipts;
  const proofSummary = [
    {
      label: "What was checked",
      value:
        detail.claims.length > 0
          ? `${detail.claims.length} claim${detail.claims.length === 1 ? "" : "s"} and ${detail.receipts.length} check receipt${detail.receipts.length === 1 ? "" : "s"}`
          : "no bound claim rows yet",
      detail:
        firstClaim != null
          ? readablePredicateText(firstClaim.predicate)
          : "This run needs asserted claims before the pack can explain the work.",
    },
    {
      label: "Who claimed / verified",
      value: `${detail.ownerLabel ?? "actor unknown"} -> ${
        latestJudgment?.verifierAgent ?? detail.verifierLabel ?? "verifier unknown"
      }`,
      detail:
        latestJudgment?.note?.trim() ||
        (detail.verdict ? `Current decision: ${detail.verdict}` : "No verifier decision recorded yet."),
    },
    {
      label: "Why it passed or failed",
      value:
        detail.receipts.length > 0
          ? `${passedReceipts}/${detail.receipts.length} checks passed`
          : "no check receipts",
      detail:
        failedReceipts > 0
          ? `${failedReceipts} historical check receipt${failedReceipts === 1 ? "" : "s"} failed; inspect run history for the rejection and rework.`
          : "No failing check receipt is visible in this evidence set.",
    },
    {
      label: "How to replay",
      value: v?.ok ? "bundle checks pass" : "verification unavailable",
      detail:
        v?.ok
          ? `Re-derived ${v.checks.length} bundle check${v.checks.length === 1 ? "" : "s"} from preserved evidence.`
          : "Download the metadata bundle for review. Receipt integrity cannot be established without an envelope and independent trust material.",
    },
  ];

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-2 sm:flex-row sm:items-start sm:justify-between">
          <div>
            <CardTitle>Evidence pack</CardTitle>
            <CardDescription>
              A metadata bundle for the claim, checks, verifier decision, and reported trust state.
            </CardDescription>
          </div>
          {b.found ? (
            <IntentBadge intent={trustIntent(b.trust)}>
              {trustHeadline(b.trust)}
            </IntentBadge>
          ) : null}
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        <div className="grid gap-px overflow-hidden rounded-lg border border-border bg-border md:grid-cols-2">
          {proofSummary.map((item) => (
            <div key={item.label} className="bg-card px-3 py-3">
              <div className="type-subtext font-medium uppercase tracking-normal text-muted-foreground">
                {item.label}
              </div>
              <div className="mt-1 type-body font-medium text-foreground">{item.value}</div>
              <p className="mt-1 line-clamp-2 type-subtext text-muted-foreground">
                {item.detail}
              </p>
            </div>
          ))}
        </div>

        {!b.found ? (
          <p className="type-body text-muted-foreground">
            Bundle not composable: <code>{b.reason}</code>
          </p>
        ) : (
          <div className="space-y-3">
            <div className="rounded-lg border border-border bg-muted/15 p-3">
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div>
                  <div className="type-subtext font-medium uppercase tracking-normal text-muted-foreground">
                    Trust root
                  </div>
                  <div className="mt-1 type-body font-medium text-foreground">
                    {trustHeadline(b.trust)}
                  </div>
                </div>
                <IntentBadge intent={trustIntent(b.trust)}>{b.trust.status}</IntentBadge>
              </div>
              <p className="mt-2 type-body text-muted-foreground">
                {trustExplanation(b.trust)}
              </p>
              <p className="mt-1 type-subtext text-muted-foreground">
                Trust detail: {b.trust.summary}
              </p>
            </div>

            <dl className="grid gap-2 type-body sm:grid-cols-2">
              <div className="rounded-md border border-border bg-background p-3">
                <dt className={MUTED}>Bundle hash</dt>
                <dd>
                  <code className="type-subtext break-all">{b.bundleHash}</code>
                </dd>
              </div>
              <div className="rounded-md border border-border bg-background p-3">
                <dt className={MUTED}>Pack contents</dt>
                <dd className={NUM}>
                  {b.claimCount} claims · {b.receiptCount} check receipts · {b.byteLength} bytes
                </dd>
              </div>
              <div className="rounded-md border border-border bg-background p-3">
                <dt className={MUTED}>Chain head</dt>
                <dd>
                  <code className="type-subtext" title={b.chainHead ?? undefined}>
                    {shortHash(b.chainHead, 16)}
                  </code>
                </dd>
              </div>
              <div className="rounded-md border border-border bg-background p-3">
                <dt className={MUTED}>Bundle consistency checks</dt>
                <dd className="type-subtext text-muted-foreground">
                  bundle hash · claim chain · check receipts · reported signature marker
                </dd>
              </div>
              {b.aidRef ? (
                <div className="rounded-md border border-border bg-background p-3 sm:col-span-2">
                  <dt className={MUTED}>AID ref</dt>
                  <dd>
                    <code className="type-subtext break-all">{b.aidRef}</code>
                  </dd>
                </div>
              ) : null}
              {b.trust.publicKey ? (
                <div className="rounded-md border border-border bg-background p-3 sm:col-span-2">
                  <dt className={MUTED}>Signing public key</dt>
                  <dd>
                    <code className="type-subtext break-all">{b.trust.publicKey}</code>
                  </dd>
                </div>
              ) : null}
            </dl>
          </div>
        )}

        <div className="flex flex-wrap items-center gap-2">
          <Button variant="outline" onClick={() => void download()} disabled={busy || !b.found}>
            Download bundle JSON
          </Button>
          <Button variant="outline" onClick={() => void reverify()} disabled={busy}>
            Check bundle consistency
          </Button>
          {v ? (
            <IntentBadge intent={v.ok ? "success" : "danger"}>
              {v.ok ? "verify ok" : "verify fail"}
            </IntentBadge>
          ) : null}
        </div>
        {msg ? <p className={`${MUTED} break-words`}>{msg}</p> : null}

        {/* Production evidence.artifact affordance (P2-U3) */}
        <Alert className="type-subtext">
          <AlertTitle className="mb-1 font-medium text-foreground">
            Evidence artifact
          </AlertTitle>
          <AlertDescription className="type-subtext">
          {detail.evidenceArtifact.found ? (
            <div className="space-y-1 text-muted-foreground">
              <div>
                <Link
                  href={detail.evidenceArtifact.href ?? "#"}
                  className="text-primary hover:underline"
                >
                  Open scrubbed artifact
                </Link>
                {detail.evidenceArtifact.bodyHash ? (
                  <span className="ml-2">
                    hash <code>{shortHash(detail.evidenceArtifact.bodyHash, 12)}</code>
                  </span>
                ) : null}
              </div>
              <div>
                verifier {detail.evidenceArtifact.verifier ?? "—"} · emitted{" "}
                {detail.evidenceArtifact.emittedAt
                  ? formatRelativeTime(
                      new Date(detail.evidenceArtifact.emittedAt).toISOString(),
                    )
                  : "—"}
                {detail.evidenceArtifact.caller ? (
                  <span> · caller <code>{detail.evidenceArtifact.caller}</code></span>
                ) : null}
              </div>
            </div>
          ) : (
            <p className="text-muted-foreground">
              No evidence artifact has been emitted for this verified run yet.
            </p>
          )}
          </AlertDescription>
        </Alert>
      </CardContent>
    </Card>
  );
}

/**
 * Prove it is the one primary action on this surface. One click from the
 * decision card composes the evidence pack and seals it through the INV-8
 * export door. A pack without an audit row is rendered as a failure, never
 * a success.
 */
function ProveItControl({
  taskId,
  onProved,
}: {
  taskId: number;
  onProved?: () => void;
}) {
  const [busy, setBusy] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);
  const [okMsg, setOkMsg] = React.useState<string | null>(null);

  async function prove() {
    setBusy(true);
    setError(null);
    setOkMsg(null);
    try {
      const res = await apiCall(evidence.pack, { params: { id: taskId } });
      if (!res.ok) {
        setError(res.reason);
        return;
      }
      if (res.auditRowId == null || !res.bodyHash) {
        setError(
          "Pack was produced without an export.emitted audit row — refusing to treat that as proof (INV-8).",
        );
        return;
      }
      for (const name of res.fileNames) {
        const body = res.files[name];
        if (body == null) continue;
        const blob = new Blob([body], {
          type: name.endsWith(".json") ? "application/json" : "text/markdown",
        });
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = `${res.folderName}__${name.replace(/\//g, "__")}`;
        a.click();
        URL.revokeObjectURL(url);
      }
      setOkMsg(
        `Pack downloaded (${res.fileNames.length} files) · audit #${res.auditRowId} · ${res.trust.summary}`,
      );
      onProved?.();
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="space-y-2">
      <Button variant="default" onClick={() => void prove()} disabled={busy}>
        Prove it
      </Button>
      {error ? (
        <Alert variant="destructive">
          <AlertTitle>Could not prove it</AlertTitle>
          <AlertDescription>{error}</AlertDescription>
        </Alert>
      ) : null}
      {okMsg ? <p className={`${MUTED} break-words`}>{okMsg}</p> : null}
    </div>
  );
}


function DecisionCard({
  detail,
  onDecided,
}: {
  detail: EvidenceTaskDetailDto;
  onDecided: () => void;
}) {
  const [busy, setBusy] = React.useState(false);
  const [err, setErr] = React.useState<string | null>(null);
  const inReview = detail.status === "review";

  async function act(decision: "accept" | "reject") {
    setBusy(true);
    setErr(null);
    try {
      let reason: string | undefined;
      if (decision === "reject") {
        reason = promptDecisionReason("reject");
        if (reason === undefined) {
          setBusy(false);
          return;
        }
      }
      // THE write path — review-decision.ts → override.human. No parallel approve.
      await decideTaskReview({
        taskId: detail.taskId,
        projectSlug: detail.projectSlug,
        sectionSlug: detail.sectionSlug,
        decision,
        reason,
      });
      onDecided();
    } catch (e) {
      setErr(e instanceof Error ? e.message : String(e));
    } finally {
      setBusy(false);
    }
  }

  /**
   * R1 (#45973, VERIFICATION-SURFACE-PRD): the headline is derived from the
   * verdict SEQUENCE, not `detail.verdict` — the terminal scalar. 57 tasks in
   * the vault carry a reject AND a later approve; headlining only the scalar
   * renders every one of them as a clean single-pass accept, which is
   * indistinguishable from a rubber stamp. `judgments` already ships in this
   * same payload, so this costs no contract, endpoint, or migration.
   */
  const rework = React.useMemo(() => {
    const rejects = detail.judgments.filter((j) => j.verdict === "reject");
    // Narrow via the element, not via `.length` — `noUncheckedIndexedAccess`
    // does not treat a non-zero length as proof the index is inhabited.
    const last = rejects[rejects.length - 1];
    if (!last) return null;
    return {
      rejectCount: rejects.length,
      lastRejectNote: last.note,
      lastRejectVerifier: last.verifierAgent,
    };
  }, [detail.judgments]);

  const evidenceSlot = (
    <div className="space-y-1">
      <div>
        Consequence:{" "}
        <IntentBadge intent={consequenceIntent(detail.consequenceTag)}>
          {detail.consequenceTag ?? "none"}
        </IntentBadge>
        {detail.consequenceBasis ? (
          <span className="ml-2 text-muted-foreground">{detail.consequenceBasis}</span>
        ) : null}
      </div>
      {detail.evidenceArtifact.found && detail.evidenceArtifact.href ? (
        <div>
          Artifact:{" "}
          <Link href={detail.evidenceArtifact.href} className="text-primary hover:underline">
            view / copy
          </Link>
          {detail.evidenceArtifact.bodyHash ? (
            <code className="ml-2">{shortHash(detail.evidenceArtifact.bodyHash)}</code>
          ) : null}
        </div>
      ) : null}
      {/*
       * R4 (#45973): no "No evidence artifact yet" line here. The Evidence
       * bundle card below already states the same absence, and states it BETTER
       * — it names the row (`evidence.artifact`) and when it appears ("emitted
       * on verified done"). Two renderings of one absence, ~350px apart, read as
       * two separate problems. The decision hero carries the DECISION; an empty
       * slot is not a fact worth a line.
       */}
      {detail.bundle.found ? (
        <div>
          Bundle <code>{shortHash(detail.bundle.bundleHash, 12)}</code> ·{" "}
          {detail.bundle.trust.summary}
          {detail.verify ? (
            <span>
              {" "}
              · verify {detail.verify.ok ? "ok" : "fail"}
            </span>
          ) : null}
        </div>
      ) : null}
    </div>
  );

  return (
    <Card>
      <CardHeader>
        <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
          <CardTitle>Verifier decisions</CardTitle>
          <ProveItControl taskId={detail.taskId} onProved={onDecided} />
        </div>
      </CardHeader>
      <CardContent className="space-y-3">
        {inReview ? (
          <Decision
            prompt="Verify this outcome? (shipper ≠ verifier — override.human records the decision)"
            evidence={evidenceSlot}
            ownerLabel={detail.ownerLabel}
            verifierLabel={detail.verifierLabel}
            acceptLabel="Accept"
            rejectLabel="Reject"
            rework={rework}
            busy={busy}
            onAccept={() => void act("accept")}
            onReject={() => void act("reject")}
          />
        ) : (
          <Decision
            readOnly
            verdict={detail.verdict}
            rework={rework}
            ownerLabel={detail.ownerLabel}
            verifierLabel={detail.verifierLabel}
            evidence={evidenceSlot}
          />
        )}
        {err ? <p className="type-subtext text-destructive">{err}</p> : null}
      </CardContent>
    </Card>
  );
}

function AgentsVerificationDetailPage() {
  const { taskId: taskIdParam } = agentsVerificationDetailRoute.useParams();
  const taskId = Number(taskIdParam);
  const { data, error, isLoading, mutate } = useApi(evidence.get, {
    params: { id: taskId },
  });

  if (!Number.isInteger(taskId) || taskId <= 0) {
    return (
      <div className="px-6 py-8">
        <p className="type-body text-destructive">Invalid workflow run id.</p>
      </div>
    );
  }

  return (
    <div className="px-6 py-8 space-y-6">
      <PageHeader
        title={data ? `Run #${data.taskId} ${data.title}` : `Run #${taskId}`}
        description={
          data
            ? `${data.projectName ?? data.projectSlug} · ${data.sectionSlug} · verification evidence surface`
            : "Loading verification run..."
        }
        breadcrumbs={[
          { label: "Verification", href: "/agents/verification" },
          { label: `#${taskId}` },
        ]}
      />

      {isLoading && !data ? (
        <p className="type-body text-muted-foreground">Loading verification run...</p>
      ) : error ? (
        <Card>
          <CardContent className="pt-6">
            <p className="type-body text-destructive">
              {error instanceof Error ? error.message : String(error)}
            </p>
            <Link href="/agents/verification" className="type-subtext text-primary hover:underline mt-2 inline-block">
              ← Back to list
            </Link>
          </CardContent>
        </Card>
      ) : data ? (
        <>
          <div className="grid gap-4 sm:grid-cols-4">
            <StatCard
              title="Status"
              value={data.status}
              leadingDot={
                data.status === "done"
                  ? "success"
                  : data.status === "review"
                    ? "warning"
                    : "neutral"
              }
            />
            <StatCard
              title="Claims"
              value={String(data.claims.length)}
              description="asserted workflow claims"
            />
            <StatCard
              title="Check receipts"
              value={String(data.receipts.length)}
              description="recorded verifier checks"
            />
            <StatCard
              title="Consequence"
              value={data.consequenceTag ?? "none"}
              leadingDot={
                consequenceIntent(data.consequenceTag) === "danger"
                  ? "danger"
                  : consequenceIntent(data.consequenceTag) === "warning"
                    ? "warning"
                    : "neutral"
              }
            />
          </div>

          <div id="proof-story">
            <ProofStoryCard detail={data} />
          </div>
          <Card id="run-history">
            <CardHeader>
              <CardTitle>Run history</CardTitle>
              <CardDescription>
                The claim, rejection, rework, final decision, receipts, and bundle events in replay order.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <VerificationRunHistory rows={data.runHistory} />
            </CardContent>
          </Card>
          <div id="prove-it">
            <DecisionCard detail={data} onDecided={() => void mutate()} />
          </div>
          <ResidualGapsCard report={data.residualGaps} title="Residual gaps" />
          <div id="evidence-pack">
            <EvidenceBundleCard detail={data} onDownloaded={() => void mutate()} />
          </div>

          <Card>
            <CardHeader>
              <CardTitle>Checking waterfall</CardTitle>
            </CardHeader>
            <CardContent className="space-y-3">
              <p className="type-body text-muted-foreground">{data.waterfall.summary}</p>
              <CheckingWaterfallTable rows={data.waterfall.rows} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Claims and falsifiers</CardTitle>
            </CardHeader>
            <CardContent>
              <ClaimsTable claims={data.claims} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Verdicts</CardTitle>
            </CardHeader>
            <CardContent>
              <JudgmentsTable judgments={data.judgments} />
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle>Check receipts</CardTitle>
            </CardHeader>
            <CardContent>
              <ReceiptsTable receipts={data.receipts} />
            </CardContent>
          </Card>
        </>
      ) : null}
    </div>
  );
}

// ── Routes ───────────────────────────────────────────────────────────────────

function hopReviewVerdictEvidence(location: { pathname: string; searchStr: string }) {
  const dest = resolveReviewVerdictEvidenceRedirect(location.pathname, location.searchStr);
  if (dest) throw redirect({ to: dest, replace: true });
}

export const agentsVerificationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/verification",
  validateSearch: verificationSearchSchema,
  beforeLoad: ({ location }) => hopLeftoverAgentsToDashboard(location),
  component: AgentsVerificationPage,
});

export const agentsVerificationDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/agents/verification/$taskId",
  beforeLoad: ({ location }) => hopLeftoverAgentsToDashboard(location),
  component: AgentsVerificationDetailPage,
});

export const verificationRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/verification",
  validateSearch: verificationSearchSchema,
  beforeLoad: ({ location }) => {
    hopReviewVerdictEvidence(location);
    throw redirect({
      to: "/",
      search: (new URLSearchParams(
        location.searchStr.startsWith("?") ? location.searchStr.slice(1) : location.searchStr,
      ).get("env") === "real"
        ? { env: "real" }
        : {}) as never,
      replace: true,
    });
  },
});

export const verificationDetailRoute = createRoute({
  getParentRoute: () => rootRoute,
  path: "/verification/$taskId",
  beforeLoad: ({ location }) => {
    hopLeftoverLegacyRoute(location);
  },
});
